import { deriveProgress, type ProgressStep, type ReviewKind } from '../../shared/progress';
import type { ScriptStatus } from '../../shared/types';

interface Props {
  status: ScriptStatus;
  publishedRound?: number | null;
  /** Swaps the first node's noun: `Brief drafted` on an interview brief. */
  kind?: ReviewKind;
}

// The client-facing SCRIPT journey tracker — Script drafted → Version N
// finalized → Your review → Approved. Production logistics (shoot, edit,
// delivery) live on the client dashboard, not here. Not rendered on the
// locked/draft page (a draft returns the "being revised" screen before this
// ever mounts). The round number and reply-by deadline used to render as a
// sub-label/line under this tracker; both now live in the review card below
// it instead, so this component only ever renders the four nodes.
export default function ProgressTracker({ status, kind = 'script', publishedRound }: Props) {
  const { steps } = deriveProgress(status, kind, publishedRound);

  return (
    <div data-print-hide className="mt-4 md:mt-5 max-w-[380px]">
      <ol className="flex items-start">
        {steps.map((step, i) => (
          <Node key={step.key} step={step} first={i === 0} />
        ))}
      </ol>
    </div>
  );
}

// Mobile drops to shorter labels ("Drafted", "V2 finalized") so four nodes
// still fit a phone width without wrapping — same information, tighter
// words. Desktop keeps the full label. "Drafted" carries no noun, so it is
// right for a script and a brief alike.
function abbreviateLabel(step: ProgressStep): string {
  if (step.key === 'drafted') return 'Drafted';
  if (step.key === 'finalized') {
    return step.label === 'Version finalized'
      ? 'Finalized'
      : step.label.replace(/^Version (\d+) finalized$/, 'V$1 finalized');
  }
  return step.label;
}

function Node({ step, first }: { step: ProgressStep; first: boolean }) {
  const done = step.state === 'done';
  const current = step.state === 'current';
  return (
    <li className="flex-1 flex flex-col items-center text-center min-w-0 relative px-0.5">
      {!first && (
        <span
          aria-hidden
          className={`absolute top-[8px] h-[2px] w-full ${
            step.state === 'todo' ? 'bg-faint/25' : 'bg-accent'
          }`}
          style={{ left: '-50%' }}
        />
      )}
      <span
        className={`
          relative z-[1] w-[17px] h-[17px] rounded-full border-2 flex items-center justify-center
          ${done ? 'bg-accent border-accent text-accent-fg' : ''}
          ${current ? 'border-accent bg-card shadow-[0_0_0_3px_var(--color-accent-glow-soft)]' : ''}
          ${!done && !current ? 'border-faint/40 bg-card' : ''}
        `}
      >
        {done && (
          <svg viewBox="0 0 12 12" className="w-[9px] h-[9px]" fill="none" aria-hidden>
            <path
              d="M2.5 6.2l2.2 2.2L9.5 3.6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {current && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
      </span>
      <span
        className={`text-[11px] font-semibold mt-1.5 leading-tight ${
          current ? 'text-accent' : done ? 'text-muted' : 'text-faint'
        }`}
      >
        <span className="hidden md:inline">{step.label}</span>
        <span className="md:hidden">{abbreviateLabel(step)}</span>
      </span>
    </li>
  );
}
